import type { CardDragPreview, WallCard, WallConnection } from '@walls/store';
import { getBoardCardRectMap } from '../positioning/cardRects';
import type { BoardRect } from '../selection/utils/selectionGeometry';

export interface ConnectionPoint {
  side: 'east' | 'north' | 'south' | 'west';
  x: number;
  y: number;
}

export interface ConnectionLinePoints {
  endPoint: ConnectionPoint;
  startPoint: ConnectionPoint;
}

export interface RenderedConnection {
  connection: WallConnection;
  endCard: WallCard;
  linePoints: ConnectionLinePoints;
  startCard: WallCard;
}

export function getRenderedConnections(
  cards: WallCard[],
  connections: WallConnection[],
  measuredRects: Record<string, BoardRect> = {}
): RenderedConnection[] {
  const cardIndex = indexCards(cards);
  const cardRects = getBoardCardRectMap(cards);

  return connections.flatMap((connection) => {
    const startCardId = connection.start?.cardId;
    const endCardId = connection.end?.cardId;
    const startCard = startCardId ? cardIndex.get(startCardId) : null;
    const endCard = endCardId ? cardIndex.get(endCardId) : null;

    if (!startCard || !endCard) {
      return [];
    }

    const startRect = measuredRects[startCard.id] ?? cardRects.get(startCard.id);
    const endRect = measuredRects[endCard.id] ?? cardRects.get(endCard.id);

    if (!startRect || !endRect) {
      return [];
    }

    return [
      {
        connection,
        endCard,
        linePoints: getConnectionLinePoints(startCard, startRect, endCard, endRect),
        startCard,
      },
    ];
  });
}

export function getCardDragPreviewRects(
  measuredRects: Record<string, BoardRect>,
  cardDragPreviews: Record<string, CardDragPreview>
): Record<string, BoardRect> {
  const draggedCardIds = Object.keys(cardDragPreviews);

  if (draggedCardIds.length === 0) {
    return measuredRects;
  }

  return draggedCardIds.reduce<Record<string, BoardRect>>(
    (rects, cardId) => {
      const rect = measuredRects[cardId];
      const preview = cardDragPreviews[cardId];

      if (!rect) {
        return rects;
      }

      rects[cardId] = {
        ...rect,
        x: rect.x + preview.deltaX,
        y: rect.y + preview.deltaY,
      };

      return rects;
    },
    { ...measuredRects }
  );
}

export function getConnectionLinePoints(
  startCard: WallCard,
  startRect: BoardRect,
  endCard: WallCard,
  endRect: BoardRect
): ConnectionLinePoints {
  const startCenter = getRectCenter(startRect);
  const endCenter = getRectCenter(endRect);
  const endPosition = [
    endCenter.y < startCenter.y ? 'north' : 'south',
    endCenter.x < startCenter.x ? 'west' : 'east',
  ];
  let startSide: ConnectionPoint['side'];
  let endSide: ConnectionPoint['side'];
  const isVertical = endPosition.includes('east')
    ? endRect.x < startRect.x + startRect.width
    : endRect.x + endRect.width > startRect.x;

  if (isVertical) {
    if (endPosition.includes('north')) {
      startSide = 'north';
      endSide = 'south';
    } else {
      startSide = 'south';
      endSide = 'north';
    }
  } else if (endPosition.includes('east')) {
    startSide = 'east';
    endSide = 'west';
  } else {
    startSide = 'west';
    endSide = 'east';
  }

  return {
    endPoint: getShapePoint(endSide, endCard, endCenter, endRect),
    startPoint: getShapePoint(startSide, startCard, startCenter, startRect),
  };
}

export function getConnectionPointLinePoints(
  startCard: WallCard,
  startRect: BoardRect,
  endPoint: { x: number; y: number }
): ConnectionLinePoints {
  const startCenter = getRectCenter(startRect);
  const pointPosition = [
    endPoint.y < startCenter.y ? 'north' : 'south',
    endPoint.x < startCenter.x ? 'west' : 'east',
  ];
  const isVertical = pointPosition.includes('east')
    ? endPoint.x < startRect.x + startRect.width
    : endPoint.x > startRect.x;
  let startSide: ConnectionPoint['side'];

  if (isVertical) {
    startSide = pointPosition.includes('north') ? 'north' : 'south';
  } else {
    startSide = pointPosition.includes('east') ? 'east' : 'west';
  }

  return {
    endPoint: { side: startSide, x: endPoint.x, y: endPoint.y },
    startPoint: getShapePoint(startSide, startCard, startCenter, startRect),
  };
}

export function getConnectorPoints({ endPoint, startPoint }: ConnectionLinePoints) {
  return [
    `${Math.floor(startPoint.x)},${Math.floor(startPoint.y)}`,
    `${Math.floor(getOffsetPoint(startPoint).x)},${Math.floor(getOffsetPoint(startPoint).y)}`,
    `${Math.floor(getOffsetPoint(endPoint).x)},${Math.floor(getOffsetPoint(endPoint).y)}`,
    `${Math.floor(endPoint.x)},${Math.floor(endPoint.y)}`,
  ];
}

export function getPointConnectorPoints({ endPoint, startPoint }: ConnectionLinePoints) {
  return [
    `${Math.floor(startPoint.x)},${Math.floor(startPoint.y)}`,
    `${Math.floor(endPoint.x)},${Math.floor(endPoint.y)}`,
  ];
}

export function getConnectionPosition({ endPoint, startPoint }: ConnectionLinePoints) {
  return {
    x: startPoint.x + (endPoint.x - startPoint.x) / 2,
    y: startPoint.y + (endPoint.y - startPoint.y) / 2,
  };
}

export function getConnectionDistanceFromPoint(
  linePoints: ConnectionLinePoints,
  x: number,
  y: number
) {
  const atob = {
    x: linePoints.endPoint.x - linePoints.startPoint.x,
    y: linePoints.endPoint.y - linePoints.startPoint.y,
  };
  const atop = { x: x - linePoints.startPoint.x, y: y - linePoints.startPoint.y };
  const length = atob.x * atob.x + atob.y * atob.y;
  const t =
    length === 0 ? 0 : Math.min(1, Math.max(0, (atop.x * atob.x + atop.y * atob.y) / length));
  const linePointX = linePoints.startPoint.x + atob.x * t;
  const linePointY = linePoints.startPoint.y + atob.y * t;

  return {
    x: Math.abs(linePointX - x),
    y: Math.abs(linePointY - y),
  };
}

export function hitTestConnection(
  linePoints: ConnectionLinePoints,
  x: number,
  y: number,
  tolerance = 8
) {
  const distance = getConnectionDistanceFromPoint(linePoints, x, y);

  return distance.x < tolerance && distance.y < tolerance;
}

export function connectionsAreBetweenCards(
  connection: WallConnection,
  startCardId: string,
  endCardId: string
) {
  return (
    (connection.start?.cardId === startCardId && connection.end?.cardId === endCardId) ||
    (connection.start?.cardId === endCardId && connection.end?.cardId === startCardId)
  );
}

function indexCards(cards: WallCard[], cardIndex = new Map<string, WallCard>()) {
  cards.forEach((card) => {
    cardIndex.set(card.id, card);
    indexCards(card.cards ?? [], cardIndex);
  });

  return cardIndex;
}

function getRectCenter(rect: { height: number; width: number; x: number; y: number }) {
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

function getShapePoint(
  side: ConnectionPoint['side'],
  card: WallCard,
  cardCenter: { x: number; y: number },
  rect: { height: number; width: number; x: number; y: number }
): ConnectionPoint {
  switch (side) {
    case 'north':
      return { side, x: cardCenter.x, y: rect.y };
    case 'south':
      return { side, x: cardCenter.x, y: rect.y + rect.height };
    case 'east':
      return card.shape === 'triangle'
        ? { side, x: (cardCenter.x + rect.x + rect.width) / 2, y: cardCenter.y }
        : { side, x: rect.x + rect.width, y: cardCenter.y };
    case 'west':
      return card.shape === 'triangle'
        ? { side, x: (cardCenter.x + rect.x) / 2, y: cardCenter.y }
        : { side, x: rect.x, y: cardCenter.y };
  }
}

function getOffsetPoint(point: ConnectionPoint) {
  switch (point.side) {
    case 'north':
      return { x: point.x, y: point.y - 15 };
    case 'south':
      return { x: point.x, y: point.y + 15 };
    case 'east':
      return { x: point.x + 15, y: point.y };
    case 'west':
      return { x: point.x - 15, y: point.y };
  }
}
